/**
 * The other half of the access policy (see ./route-access): the ENTRY screens — /login and /register — which a
 * signed-in visitor who has finished onboarding has no business on. Kept apart from `resolveRouteAccess` because
 * the destination is `resolvePostLoginPath`'s to decide, and that reads the whole user, not the flags the
 * route policy needs.
 */
import { resolvePostLoginPath, type PostLoginDestination, type RedirectUser } from 'shared/post-login'

export const ENTRY_ROUTE_PREFIXES = ['/_app/_public/login', '/_app/register']

// Landing on one of these means onboarding is UNFINISHED, and the entry screen is then the visitor's to use —
// `/register/company` and `/register/applicant` are the wizards themselves.
const ONBOARDING_DESTINATIONS = new Set([
  '/register/applicant',
  '/register/company',
  '/test/competences',
  '/test/values',
  '/test/personality',
])

const isEntryRoute = (routeId: string) => ENTRY_ROUTE_PREFIXES.some((prefix) => routeId.startsWith(prefix))

/**
 * Where a signed-in visitor on an entry screen belongs, or `null` to leave them there. Null for everyone logged
 * out, and for a half-registered user — whose destination IS an onboarding step, so bouncing them would either
 * land them back where they started or take the wizard away from them.
 */
export function resolveEntryRedirect(input: {
  routeId: string
  user: RedirectUser | null
  redirect?: string
}): PostLoginDestination | null {
  const { routeId, user, redirect } = input
  if (!user || !isEntryRoute(routeId)) return null
  // Classified WITHOUT the redirect, because post-login threads an unfinished user's target through their next
  // onboarding step — `{ href: '/register/company?redirect=…' }` — and reading that as a destination would bounce
  // the company wizard onto itself.
  const home = resolvePostLoginPath(user)
  if ('to' in home && ONBOARDING_DESTINATIONS.has(home.to)) return null
  return resolvePostLoginPath(user, { redirect })
}
